import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  FileText, Plus, Trash2, Save, X, ChevronRight, ChevronDown, 
  Folder, FolderOpen, File, Globe, ExternalLink, Type, Layout,
  ArrowLeft, Maximize2, Home, FileEdit, Layers, GripVertical,
  Sparkles, ImagePlus, FolderPlus, Edit, MoveRight
} from 'lucide-react';
import axios from 'axios';
import RichTextEditor from '../components/RichTextEditor';
import InlineStructuredContentEditor from '../components/InlineStructuredContentEditor';
import CMSImagePicker from '../components/CMSImagePicker';

// Tree Node Component for page navigation
const TreeNode = ({ nodeKey, node, level, expandedNodes, toggleNode, selectedPage, selectPage, pages, onContextMenu }) => {
  const hasChildren = node.children && Object.keys(node.children).length > 0;
  const isExpanded = expandedNodes.has(nodeKey);
  const isSelected = selectedPage === node.path;
  const hasContent = pages.some(p => p.path === node.path);
  
  const paddingLeft = level * 16 + 8;
  
  const handleContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onContextMenu(e, node, nodeKey);
  };
  
  return (
    <div>
      <div
        className={`flex items-center gap-1 py-1.5 px-2 rounded-lg cursor-pointer transition-colors ${
          isSelected ? 'bg-theme-blue text-white' : 'text-gray-700 hover:bg-gray-100'
        }`}
        style={{ paddingLeft: `${paddingLeft}px` }}
        onClick={() => {
          if (hasChildren) {
            toggleNode(nodeKey);
          }
          if (node.path) {
            selectPage(node.path);
          }
        }}
        onContextMenu={handleContextMenu}
      >
        {hasChildren ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleNode(nodeKey);
            }}
            className={`p-0.5 rounded ${isSelected ? 'hover:bg-blue-600' : 'hover:bg-gray-200'}`}
          >
            {isExpanded ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </button>
        ) : (
          <span className="w-4" />
        )}
        
        {hasChildren ? (
          isExpanded ? (
            <FolderOpen className={`h-4 w-4 flex-shrink-0 ${isSelected ? 'text-white' : 'text-yellow-500'}`} />
          ) : (
            <Folder className={`h-4 w-4 flex-shrink-0 ${isSelected ? 'text-white' : 'text-yellow-500'}`} />
          )
        ) : (
          <File className={`h-4 w-4 flex-shrink-0 ${isSelected ? 'text-white' : 'text-gray-400'}`} />
        )}
        
        <span className="text-sm truncate flex-1">{node.label}</span>
        
        {hasContent && (
          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${isSelected ? 'bg-green-300' : 'bg-green-500'}`} title="Has content" />
        )}
      </div>
      
      {hasChildren && isExpanded && (
        <div>
          {Object.entries(node.children).map(([childKey, childNode]) => (
            <TreeNode
              key={childKey}
              nodeKey={childKey}
              node={childNode}
              level={level + 1}
              expandedNodes={expandedNodes}
              toggleNode={toggleNode}
              selectedPage={selectedPage}
              selectPage={selectPage}
              pages={pages}
              onContextMenu={onContextMenu}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const CMSEditor = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialPage = searchParams.get('page');
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pages, setPages] = useState([]);
  const [folders, setFolders] = useState([]);
  const [pageStructure, setPageStructure] = useState({});
  const [selectedPage, setSelectedPage] = useState(initialPage || null);
  const [editingPage, setEditingPage] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState(new Set(['about', 'services', 'patient']));
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  
  // Available components
  const [availableComponents, setAvailableComponents] = useState([]);
  
  // Context menu state
  const [contextMenu, setContextMenu] = useState({ show: false, x: 0, y: 0, node: null, nodeKey: null, isFolder: false });
  
  // New page modal state
  const [showNewPageModal, setShowNewPageModal] = useState(false);
  const [newPageType, setNewPageType] = useState(null); // 'home' or 'inner'
  const [newPageForm, setNewPageForm] = useState({
    path: '',
    title: '',
    badge_text: '',
    description: '',
    hero_image: '',
    main_content: '',
    layout: 'default',
    meta_title: '',
    meta_description: '',
    is_published: false
  });

  // AI Content Generator state
  const [generatorUrl, setGeneratorUrl] = useState('');
  const [generatingContent, setGeneratingContent] = useState(false);
  const [importImages, setImportImages] = useState(false);
  const [enableWebResearch, setEnableWebResearch] = useState(false);
  const [showRawContentModal, setShowRawContentModal] = useState(false);
  const [rawContent, setRawContent] = useState('');
  const [generatingFromRaw, setGeneratingFromRaw] = useState(false);
  const [rawContentWebResearch, setRawContentWebResearch] = useState(false);

  // Folder creation modal state
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [folderName, setFolderName] = useState('');
  const [folderParentPath, setFolderParentPath] = useState('/');

  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Move page modal state
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [moveTarget, setMoveTarget] = useState(null);
  const [selectedMoveFolder, setSelectedMoveFolder] = useState('/');
  const [moving, setMoving] = useState(false);

  const contextMenuRef = useRef(null);

  const getBackendURL = () => {
    if (process.env.REACT_APP_BACKEND_URL) {
      return process.env.REACT_APP_BACKEND_URL;
    }
    const origin = window.location.origin;
    if (origin.includes('localhost:3000')) {
      return 'http://localhost:8001';
    }
    return origin;
  };

  const backendUrl = getBackendURL();

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 3000);
  };

  // AI Content Generator function
  const generateContentFromUrl = async () => {
    if (!generatorUrl) return;
    
    setGeneratingContent(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${backendUrl}/api/ai/generate-content-from-url`,
        { url: generatorUrl, import_images: importImages, enable_web_research: enableWebResearch },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        const generatedData = response.data.data;
        
        // Update the editing page with generated content
        setEditingPage(prev => ({
          ...prev,
          title: generatedData.title || prev.title,
          badge_text: generatedData.badge_text || prev.badge_text,
          description: generatedData.description || prev.description,
          main_content: generatedData.main_content || prev.main_content,
          hero_image: generatedData.hero_image || prev.hero_image
        }));
        
        showNotification('Content generated successfully! Review and save when ready.', 'success');
        setGeneratorUrl(''); // Clear the URL input
      }
    } catch (error) {
      console.error('Error generating content:', error);
      showNotification(
        error.response?.data?.detail || 'Failed to generate content. Please check the URL and try again.',
        'error'
      );
    } finally {
      setGeneratingContent(false);
    }
  };

  // Generate content from raw text
  const generateContentFromRawText = async () => {
    if (!rawContent.trim()) return;
    
    setGeneratingFromRaw(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${backendUrl}/api/ai/generate-content-from-text`,
        { raw_content: rawContent, enable_web_research: rawContentWebResearch },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        const generatedData = response.data.data;
        
        // Update the editing page with generated content
        setEditingPage(prev => ({
          ...prev,
          title: generatedData.title || prev.title,
          badge_text: generatedData.badge_text || prev.badge_text,
          description: generatedData.description || prev.description,
          main_content: generatedData.main_content || prev.main_content,
          hero_image: generatedData.hero_image || prev.hero_image
        }));
        
        showNotification('Content generated successfully! Review and save when ready.', 'success');
        setRawContent('');
        setRawContentWebResearch(false);
        setShowRawContentModal(false);
      }
    } catch (error) {
      console.error('Error generating content from raw text:', error);
      showNotification(
        error.response?.data?.detail || 'Failed to generate content. Please try again.',
        'error'
      );
    } finally {
      setGeneratingFromRaw(false);
    }
  };

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target)) {
        setContextMenu({ show: false, x: 0, y: 0, node: null, nodeKey: null, isFolder: false });
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    fetchPages();
    fetchComponents();
  }, []);

  useEffect(() => {
    if (initialPage && pageStructure && Object.keys(pageStructure).length > 0) {
      selectPage(initialPage);
    }
  }, [initialPage, pageStructure]);

  const fetchPages = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const structureResponse = await axios.get(`${backendUrl}/api/cms/page-structure`);
      setPageStructure(structureResponse.data.structure || {});
      
      const pagesResponse = await axios.get(`${backendUrl}/api/cms/pages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPages(pagesResponse.data.pages || []);
      
      // Fetch all CMS folders
      try {
        const foldersResponse = await axios.get(`${backendUrl}/api/cms/folders`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFolders(foldersResponse.data.folders || []);
      } catch (err) {
        console.error('Error fetching folders:', err);
        setFolders([]);
      }
    } catch (error) {
      console.error('Error fetching pages:', error);
      setPages([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchComponents = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${backendUrl}/api/cms/components`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableComponents(response.data.components?.filter(c => c.is_published) || []);
    } catch (error) {
      console.error('Error fetching components:', error);
      setAvailableComponents([]);
    }
  };

  const toggleNode = (nodeId) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  };

  const selectPage = async (pagePath) => {
    setSelectedPage(pagePath);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${backendUrl}/api/cms/pages${pagePath}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.page) {
        setEditingPage(response.data.page);
      } else {
        const pageInfo = findPageInStructure(pagePath);
        setEditingPage({
          path: pagePath,
          title: pageInfo?.label || 'New Page',
          badge_text: '',
          description: '',
          hero_image: '',
          main_content: '',
          layout: 'default',
          is_published: false,
          is_custom: false
        });
      }
    } catch (error) {
      console.error('Error fetching page:', error);
    }
  };

  const findPageInStructure = (path, structure = pageStructure) => {
    for (const key in structure) {
      const node = structure[key];
      if (node.path === path) {
        return node;
      }
      if (node.children) {
        const found = findPageInStructure(path, node.children);
        if (found) return found;
      }
    }
    return null;
  };

  const savePage = async () => {
    if (!editingPage) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      if (editingPage.id) {
        await axios.put(`${backendUrl}/api/cms/pages/${editingPage.id}`, editingPage, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showNotification('Page updated successfully');
      } else {
        const response = await axios.post(`${backendUrl}/api/cms/pages`, editingPage, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setEditingPage(response.data.page);
        showNotification('Page created successfully');
      }
      await fetchPages();
    } catch (error) {
      console.error('Error saving page:', error);
      showNotification(error.response?.data?.detail || 'Error saving page', 'error');
    } finally {
      setSaving(false);
    }
  };

  const deletePage = async () => {
    if (!editingPage?.id) return;
    if (!window.confirm('Are you sure you want to delete this page?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${backendUrl}/api/cms/pages/${editingPage.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditingPage(null);
      setSelectedPage(null);
      await fetchPages();
      showNotification('Page deleted successfully');
    } catch (error) {
      console.error('Error deleting page:', error);
      showNotification(error.response?.data?.detail || 'Error deleting page', 'error');
    }
  };

  // Context menu handlers
  const handleContextMenu = (e, node, nodeKey, forceIsFolder = false) => {
    e.preventDefault();
    
    // Calculate position to keep menu on screen
    const menuWidth = 220;
    const menuHeight = 250;
    let x = e.clientX;
    let y = e.clientY;
    
    // Adjust if menu would go off right edge
    if (x + menuWidth > window.innerWidth) {
      x = window.innerWidth - menuWidth - 10;
    }
    
    // Adjust if menu would go off bottom edge
    if (y + menuHeight > window.innerHeight) {
      y = window.innerHeight - menuHeight - 10;
    }
    
    // Check if node is a folder (has children OR forceIsFolder flag)
    const isFolder = forceIsFolder || (node && node.children && Object.keys(node.children).length > 0);
    
    setContextMenu({
      show: true,
      x: x,
      y: y,
      node,
      nodeKey,
      isFolder
    });
  };

  const handleCreateNewPage = (pageType) => {
    // Get the parent path from context menu node
    let parentPath = '/';
    if (contextMenu.node && contextMenu.node.path) {
      parentPath = contextMenu.node.path;
      if (!parentPath.endsWith('/')) {
        parentPath += '/';
      }
    }
    
    setNewPageType(pageType);
    setNewPageForm({
      path: parentPath,
      title: '',
      badge_text: '',
      description: '',
      hero_image: '',
      main_content: '',
      layout: pageType === 'home' ? 'home' : 'default',
      meta_title: '',
      meta_description: '',
      is_published: false
    });
    setContextMenu({ show: false, x: 0, y: 0, node: null, nodeKey: null, isFolder: false });
    setShowNewPageModal(true);
  };

  const handleCreateFolder = () => {
    // Get the parent path from context menu node
    let parentPath = '/';
    if (contextMenu.node && contextMenu.node.path) {
      parentPath = contextMenu.node.path;
      if (!parentPath.endsWith('/')) {
        parentPath += '/';
      }
    }
    
    setFolderParentPath(parentPath);
    setFolderName('');
    setContextMenu({ show: false, x: 0, y: 0, node: null, nodeKey: null, isFolder: false });
    setShowFolderModal(true);
  };

  // Create root folder function
  const handleCreateRootFolder = () => {
    setFolderParentPath('/');
    setFolderName('');
    setShowFolderModal(true);
  };

  // Submit folder creation - creates folder only (no page content)
  const handleFolderSubmit = async () => {
    if (!folderName.trim()) return;
    
    const cleanName = folderName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    const folderPath = folderParentPath + cleanName;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${backendUrl}/api/cms/folders`,
        {
          path: folderPath,
          title: folderName,
          parent_path: folderParentPath
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setShowFolderModal(false);
      setFolderName('');
      showNotification(`Folder "${folderName}" created. Right-click to add pages inside.`, 'success');
      await fetchPages(); // Refresh the page list including folders
    } catch (error) {
      console.error('Error creating folder:', error);
      showNotification(
        error.response?.data?.detail || 'Failed to create folder',
        'error'
      );
    }
  };

  // Handle delete from context menu
  const handleDeleteFromContextMenu = () => {
    if (!contextMenu.node) return;
    
    const node = contextMenu.node;
    const isFolder = node.children && Object.keys(node.children).length > 0;
    
    setDeleteTarget({
      type: isFolder ? 'folder' : 'page',
      path: node.path,
      title: node.label || node.path
    });
    setContextMenu({ show: false, x: 0, y: 0, node: null, nodeKey: null, isFolder: false });
    setShowDeleteModal(true);
  };

  // Handle edit from context menu
  const handleEditFromContextMenu = () => {
    if (!contextMenu.node) return;
    
    const node = contextMenu.node;
    selectPage(node.path);
    setContextMenu({ show: false, x: 0, y: 0, node: null, nodeKey: null, isFolder: false });
  };

  // Handle move from context menu
  const handleMoveFromContextMenu = () => {
    if (!contextMenu.node) return;
    
    const node = contextMenu.node;
    const page = pages.find(p => p.path === node.path);
    
    if (!page) {
      showNotification('Can only move custom pages', 'error');
      setContextMenu({ show: false, x: 0, y: 0, node: null, nodeKey: null, isFolder: false });
      return;
    }
    
    setMoveTarget({
      id: page.id,
      path: node.path,
      title: node.label || node.path
    });
    setSelectedMoveFolder('/');
    setContextMenu({ show: false, x: 0, y: 0, node: null, nodeKey: null, isFolder: false });
    setShowMoveModal(true);
  };

  // Confirm delete
  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      
      if (deleteTarget.type === 'folder') {
        await axios.delete(`${backendUrl}/api/cms/folders/by-path?path=${encodeURIComponent(deleteTarget.path)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showNotification(`Folder "${deleteTarget.title}" deleted successfully`, 'success');
      } else {
        await axios.delete(`${backendUrl}/api/cms/pages/by-path?path=${encodeURIComponent(deleteTarget.path)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showNotification(`Page "${deleteTarget.title}" deleted successfully`, 'success');
      }
      
      if (selectedPage === deleteTarget.path) {
        setSelectedPage(null);
        setEditingPage(null);
      }
      
      await fetchPages();
    } catch (error) {
      console.error('Error deleting:', error);
      showNotification(
        error.response?.data?.detail || `Failed to delete ${deleteTarget.type}`,
        'error'
      );
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
      setDeleteTarget(null);
    }
  };

  // Confirm move
  const handleConfirmMove = async () => {
    if (!moveTarget) return;
    
    setMoving(true);
    try {
      const token = localStorage.getItem('token');
      
      await axios.post(`${backendUrl}/api/cms/pages/move`, {
        page_id: moveTarget.id,
        target_folder_path: selectedMoveFolder
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      showNotification(`Page "${moveTarget.title}" moved successfully`, 'success');
      
      if (selectedPage === moveTarget.path) {
        setSelectedPage(null);
        setEditingPage(null);
      }
      
      await fetchPages();
    } catch (error) {
      console.error('Error moving:', error);
      showNotification(
        error.response?.data?.detail || 'Failed to move page',
        'error'
      );
    } finally {
      setMoving(false);
      setShowMoveModal(false);
      setMoveTarget(null);
    }
  };

  const handleNewPageSubmit = async () => {
    if (!newPageForm.path || !newPageForm.title) {
      showNotification('Path and Title are required', 'error');
      return;
    }

    // Ensure path starts with /
    let path = newPageForm.path;
    if (!path.startsWith('/')) {
      path = '/' + path;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const pageData = {
        ...newPageForm,
        path,
        is_custom: true,
        page_type: newPageType
      };
      
      const response = await axios.post(`${backendUrl}/api/cms/pages`, pageData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      showNotification('Page created successfully');
      setShowNewPageModal(false);
      await fetchPages();
      
      // Select the new page
      setSelectedPage(path);
      setEditingPage(response.data.page);
    } catch (error) {
      console.error('Error creating page:', error);
      showNotification(error.response?.data?.detail || 'Error creating page', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Handle right-click on empty area of tree
  const handleTreeContextMenu = (e) => {
    e.preventDefault();
    setContextMenu({
      show: true,
      x: e.clientX,
      y: e.clientY,
      node: null,
      nodeKey: null,
      isFolder: false
    });
  };

  return (
    <div className="fixed inset-0 bg-gray-100 z-50 flex flex-col">
      {/* Top Bar */}
      <div className="bg-white border-b border-gray-200 px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/content-manager')}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            title="Back to Content Manager"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="h-6 w-px bg-gray-300" />
          <h1 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Maximize2 className="h-5 w-5 text-theme-blue" />
            CMS Page Editor
          </h1>
        </div>
        
        {editingPage && (
          <div className="flex items-center gap-2">
            <a
              href={`${editingPage.path}?preview=true`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-theme-blue hover:bg-blue-50 rounded-lg transition-colors flex items-center gap-1"
            >
              <ExternalLink className="h-4 w-4" />
              Preview
            </a>
            <button
              onClick={savePage}
              disabled={saving}
              className="px-4 py-1.5 bg-theme-blue text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 flex items-center gap-2 text-sm font-medium"
            >
              {saving ? (
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Save
            </button>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Page Tree */}
        <div className="w-64 bg-white border-r border-gray-200 flex flex-col">
          <div className="p-3 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-700">Site Pages</h2>
            <span className="text-xs text-gray-400">Right-click to add</span>
          </div>
          <div 
            className="flex-1 overflow-y-auto p-2 scrollbar-thin"
            style={{ scrollbarWidth: 'thin', scrollbarColor: '#e5e7eb transparent' }}
            onContextMenu={handleTreeContextMenu}
          >
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-theme-blue mx-auto" />
              </div>
            ) : (
              <div className="space-y-1">
                {Object.entries(pageStructure).map(([key, node]) => (
                  <TreeNode
                    key={key}
                    nodeKey={key}
                    node={node}
                    level={0}
                    expandedNodes={expandedNodes}
                    toggleNode={toggleNode}
                    selectedPage={selectedPage}
                    selectPage={selectPage}
                    pages={pages}
                    onContextMenu={handleContextMenu}
                  />
                ))}
                
                {/* Custom pages section */}
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="px-2 py-1 flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-500 uppercase">
                      Custom Pages
                    </span>
                    <button
                      onClick={handleCreateRootFolder}
                      className="p-1 text-gray-400 hover:text-theme-blue hover:bg-blue-50 rounded transition-colors"
                      title="Create New Folder"
                    >
                      <FolderPlus className="h-4 w-4" />
                    </button>
                  </div>
                  
                  {/* Render custom folders with their pages */}
                  {folders.length > 0 && folders.map(folder => {
                    const folderPages = pages.filter(p => p.is_custom && p.parent_path === folder.path);
                    const isFolderExpanded = expandedNodes.has(`custom-${folder.id}`);
                    
                    return (
                      <div key={folder.id}>
                        <div
                          className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm rounded-lg text-gray-700 hover:bg-gray-100 cursor-pointer"
                          onClick={() => toggleNode(`custom-${folder.id}`)}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            handleContextMenu(e, { path: folder.path, label: folder.title }, folder.id, true);
                          }}
                        >
                          {folderPages.length > 0 ? (
                            isFolderExpanded ? (
                              <ChevronDown className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                            ) : (
                              <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                            )
                          ) : (
                            <span className="w-3.5" />
                          )}
                          {isFolderExpanded ? (
                            <FolderOpen className="h-4 w-4 flex-shrink-0 text-yellow-500" />
                          ) : (
                            <Folder className="h-4 w-4 flex-shrink-0 text-yellow-500" />
                          )}
                          <span className="truncate">{folder.title}</span>
                          {folderPages.length > 0 && (
                            <span className="text-xs text-gray-400 ml-auto">{folderPages.length}</span>
                          )}
                        </div>
                        
                        {/* Pages inside this folder */}
                        {isFolderExpanded && folderPages.map(page => (
                          <button
                            key={page.id}
                            onClick={() => selectPage(page.path)}
                            onContextMenu={(e) => {
                              e.preventDefault();
                              handleContextMenu(e, { path: page.path, label: page.title }, page.id, false);
                            }}
                            className={`w-full flex items-center gap-2 pl-10 pr-3 py-2 text-left text-sm rounded-lg transition-colors ${
                              selectedPage === page.path
                                ? 'bg-theme-blue text-white'
                                : 'text-gray-700 hover:bg-gray-100'
                            }`}
                          >
                            <File className="h-4 w-4 flex-shrink-0" />
                            <span className="truncate">{page.title}</span>
                          </button>
                        ))}
                      </div>
                    );
                  })}
                  
                  {/* Render custom pages at root level (not in any folder) */}
                  {pages.filter(p => p.is_custom && (!p.parent_path || p.parent_path === '/')).map(page => (
                    <button
                      key={page.id}
                      onClick={() => selectPage(page.path)}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        handleContextMenu(e, { path: page.path, label: page.title }, page.id, false);
                      }}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm rounded-lg transition-colors ${
                        selectedPage === page.path
                          ? 'bg-theme-blue text-white'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <File className="h-4 w-4 flex-shrink-0" />
                      <span className="truncate">{page.title}</span>
                    </button>
                  ))}
                  
                  {/* Show message if no custom content */}
                  {folders.length === 0 && pages.filter(p => p.is_custom).length === 0 && (
                    <p className="px-3 py-2 text-xs text-gray-400 italic">
                      No custom pages yet. Click + to create.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Page Editor */}
        <div className="flex-1 bg-gray-50 overflow-y-auto">
          {selectedPage && editingPage ? (
            <div className="max-w-4xl mx-auto p-6">
              {/* Page Header */}
              <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex-1">
                    <h2 className="text-xl font-semibold text-gray-900">{editingPage.title || 'Edit Page'}</h2>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-sm text-gray-500">Path:</span>
                      <input
                        type="text"
                        value={editingPage.path || ''}
                        onChange={(e) => {
                          let newPath = e.target.value;
                          // Ensure path starts with /
                          if (newPath && !newPath.startsWith('/')) {
                            newPath = '/' + newPath;
                          }
                          setEditingPage({...editingPage, path: newPath});
                        }}
                        className="flex-1 max-w-md px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                        placeholder="/page-slug"
                      />
                    </div>
                  </div>
                  {editingPage.is_custom && (
                    <button
                      onClick={deletePage}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete page"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  )}
                </div>

                {/* Published Toggle */}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingPage.is_published || false}
                    onChange={(e) => setEditingPage({...editingPage, is_published: e.target.checked})}
                    className="w-4 h-4 text-theme-blue border-gray-300 rounded focus:ring-theme-blue"
                  />
                  <span className="text-sm text-gray-700">Published</span>
                </label>
              </div>

              {/* Hero Section Editor */}
              <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Layout className="h-5 w-5 text-theme-blue" />
                  Hero Section
                </h3>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Page Title</label>
                      <input
                        type="text"
                        value={editingPage.title || ''}
                        onChange={(e) => setEditingPage({...editingPage, title: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                        placeholder="Page title"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Badge Text</label>
                      <input
                        type="text"
                        value={editingPage.badge_text || ''}
                        onChange={(e) => setEditingPage({...editingPage, badge_text: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                        placeholder="e.g., Professional Care"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Hero Description</label>
                    <textarea
                      value={editingPage.description || ''}
                      onChange={(e) => setEditingPage({...editingPage, description: e.target.value})}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                      placeholder="Brief description for the hero section"
                    />
                    <div className="mt-2">
                      <CMSImagePicker
                        value={editingPage.hero_image || ''}
                        onChange={(url) => setEditingPage({...editingPage, hero_image: url})}
                        showInline={true}
                        buttonLabel="Add Hero Image"
                      />
                    </div>
                  </div>

                  {/* Show hero image preview if set */}
                  {editingPage.hero_image && (
                    <div className="flex items-start gap-4 p-3 bg-gray-50 rounded-lg">
                      <img 
                        src={editingPage.hero_image} 
                        alt="Hero" 
                        className="w-24 h-16 object-cover rounded-lg border border-gray-200" 
                      />
                      <div className="flex-1">
                        <p className="text-xs text-gray-500 mb-1">Hero Image</p>
                        <p className="text-xs text-gray-400 truncate">{editingPage.hero_image}</p>
                      </div>
                      <button
                        onClick={() => setEditingPage({...editingPage, hero_image: ''})}
                        className="text-xs text-red-600 hover:text-red-700"
                      >
                        Remove
                      </button>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Layout</label>
                      <select
                        value={editingPage.layout || 'default'}
                        onChange={(e) => setEditingPage({...editingPage, layout: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                      >
                        <option value="default">Default (Centered Hero)</option>
                        <option value="image_right">Image Right</option>
                        <option value="full_width">Full Width</option>
                        <option value="home">Home Page Layout</option>
                      </select>
                    </div>
                    <div>
                      <label className="flex items-center gap-2 cursor-pointer mt-6">
                        <input
                          type="checkbox"
                          checked={editingPage.is_published || false}
                          onChange={(e) => setEditingPage({...editingPage, is_published: e.target.checked})}
                          className="w-4 h-4 text-theme-blue border-gray-300 rounded focus:ring-theme-blue"
                        />
                        <span className="text-sm text-gray-700">Published</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* AI Content Generator */}
              <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-purple-600" />
                  AI Content Generator
                </h3>
                <p className="text-sm text-gray-500 mb-3">
                  Enter a URL to automatically generate structured content for this page using AI.
                </p>
                <div className="flex gap-2">
                  <input
                    type="url"
                    value={generatorUrl || ''}
                    onChange={(e) => setGeneratorUrl(e.target.value)}
                    placeholder="https://example.com/page-to-scrape"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    disabled={generatingContent}
                  />
                  <button
                    onClick={generateContentFromUrl}
                    disabled={!generatorUrl || generatingContent}
                    className="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium"
                  >
                    {generatingContent ? (
                      <>
                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4" />
                        Generate
                      </>
                    )}
                  </button>
                </div>
                <div className="flex flex-wrap items-center gap-4 mt-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={importImages}
                      onChange={(e) => setImportImages(e.target.checked)}
                      className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      disabled={generatingContent}
                    />
                    <ImagePlus className="h-4 w-4 text-gray-500" />
                    <span className="text-sm text-gray-600">Import images to S3</span>
                  </label>
                  
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={enableWebResearch}
                      onChange={(e) => setEnableWebResearch(e.target.checked)}
                      className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      disabled={generatingContent}
                    />
                    <Globe className="h-4 w-4 text-gray-500" />
                    <span className="text-sm text-gray-600">Enhance with web research</span>
                  </label>
                  
                  {/* Raw Content Button */}
                  <button
                    onClick={() => setShowRawContentModal(true)}
                    className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1 ml-auto"
                  >
                    <FileText className="h-4 w-4" />
                    Paste Raw Content
                  </button>
                </div>
              </div>

              {/* Main Content Editor */}
              <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Type className="h-5 w-5 text-theme-blue" />
                  Main Content
                </h3>
                
                {/* Check if content is structured JSON and render inline editor */}
                {(() => {
                  try {
                    const parsed = editingPage.main_content 
                      ? (typeof editingPage.main_content === 'string' 
                        ? JSON.parse(editingPage.main_content) 
                        : editingPage.main_content)
                      : null;
                    
                    if (parsed && parsed.sections && Array.isArray(parsed.sections)) {
                      return (
                        <InlineStructuredContentEditor
                          content={editingPage.main_content}
                          onChange={(updatedContent) => setEditingPage({...editingPage, main_content: updatedContent})}
                        />
                      );
                    }
                    return null;
                  } catch {
                    return null;
                  }
                })() || (
                  <>
                    <p className="text-sm text-gray-500 mb-3">
                      Edit the main content area below the hero section. Use the visual editor for formatting or switch to HTML mode for code editing.
                    </p>
                    <RichTextEditor
                      value={editingPage.main_content || ''}
                      onChange={(value) => setEditingPage({...editingPage, main_content: value})}
                      placeholder="Enter the main page content here. This will replace the default content below the hero section."
                      rows={12}
                    />
                  </>
                )}
              </div>

              {/* Page Components */}
              <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Layers className="h-5 w-5 text-theme-blue" />
                  Page Components
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  Add reusable components to this page. Components will appear below the main content.
                </p>

                {/* Current Components */}
                {editingPage.components && editingPage.components.length > 0 ? (
                  <div className="space-y-2 mb-4">
                    {editingPage.components.map((comp, index) => {
                      const componentInfo = availableComponents.find(c => c.id === comp.component_id);
                      return (
                        <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <GripVertical className="h-4 w-4 text-gray-400 cursor-grab" />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">{componentInfo?.name || 'Unknown Component'}</p>
                            <p className="text-xs text-gray-500">{componentInfo?.type || comp.component_id}</p>
                          </div>
                          <select
                            value={comp.position || 'after_content'}
                            onChange={(e) => {
                              const newComponents = [...editingPage.components];
                              newComponents[index] = {...comp, position: e.target.value};
                              setEditingPage({...editingPage, components: newComponents});
                            }}
                            className="text-xs px-2 py-1 border border-gray-300 rounded"
                          >
                            <option value="after_content">After Main Content</option>
                            <option value="before_footer">Before Footer CTA</option>
                          </select>
                          <button
                            onClick={() => {
                              const newComponents = editingPage.components.filter((_, i) => i !== index);
                              setEditingPage({...editingPage, components: newComponents});
                            }}
                            className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200 mb-4">
                    <Layers className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No components added</p>
                  </div>
                )}

                {/* Add Component Dropdown */}
                {availableComponents.length > 0 ? (
                  <div className="flex items-center gap-2">
                    <select
                      id="add-component-select"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent text-sm"
                      defaultValue=""
                    >
                      <option value="" disabled>Select a component to add...</option>
                      {availableComponents.map(comp => (
                        <option key={comp.id} value={comp.id}>{comp.name} ({comp.type})</option>
                      ))}
                    </select>
                    <button
                      onClick={() => {
                        const select = document.getElementById('add-component-select');
                        const componentId = select.value;
                        if (!componentId) return;
                        const currentComponents = editingPage.components || [];
                        setEditingPage({
                          ...editingPage, 
                          components: [...currentComponents, { component_id: componentId, position: 'after_content' }]
                        });
                        select.value = '';
                      }}
                      className="px-4 py-2 bg-theme-blue text-white rounded-lg hover:bg-blue-600 text-sm font-medium flex items-center gap-1"
                    >
                      <Plus className="h-4 w-4" />
                      Add
                    </button>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">
                    No components available. <a href="/content-manager" className="text-theme-blue hover:underline">Create components</a> in the Content Manager.
                  </p>
                )}
              </div>

              {/* SEO Settings */}
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Globe className="h-5 w-5 text-theme-blue" />
                  SEO Settings
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Meta Title</label>
                    <input
                      type="text"
                      value={editingPage.meta_title || ''}
                      onChange={(e) => setEditingPage({...editingPage, meta_title: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                      placeholder="SEO title (leave empty to use page title)"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Meta Description</label>
                    <textarea
                      value={editingPage.meta_description || ''}
                      onChange={(e) => setEditingPage({...editingPage, meta_description: e.target.value})}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                      placeholder="SEO description for search engines"
                    />
                  </div>
                </div>

                {/* Page Info */}
                {editingPage.id && (
                  <div className="mt-4 pt-4 border-t border-gray-200 text-sm text-gray-500">
                    <p>Created: {editingPage.created_at ? new Date(editingPage.created_at).toLocaleString() : 'N/A'}</p>
                    <p>Last updated: {editingPage.updated_at ? new Date(editingPage.updated_at).toLocaleString() : 'N/A'}</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center h-full">
              <div className="text-center">
                <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">Select a page from the tree to edit</p>
                <p className="text-gray-400 text-sm mt-1">Or right-click to create a new page</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Context Menu */}
      {contextMenu.show && (
        <div
          ref={contextMenuRef}
          className="fixed bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-[60] min-w-[200px]"
          style={{ top: contextMenu.y, left: contextMenu.x }}
        >
          {/* Show folder context if available */}
          {contextMenu.node && (
            <div className="px-3 py-1.5 text-xs text-gray-500 border-b border-gray-100 mb-1">
              <span className="font-medium">In:</span> {contextMenu.node.path || '/'}
            </div>
          )}
          
          <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase">
            Create New
          </div>
          <button
            onClick={() => handleCreateNewPage('home')}
            className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
          >
            <Home className="h-4 w-4 text-blue-500" />
            Home Page
          </button>
          <button
            onClick={() => handleCreateNewPage('inner')}
            className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
          >
            <FileEdit className="h-4 w-4 text-green-500" />
            Inner Page
          </button>
          {/* Show New Folder option */}
          <button
            onClick={handleCreateFolder}
            className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
          >
            <FolderPlus className="h-4 w-4 text-amber-500" />
            New Folder
          </button>
          
          {/* Actions for pages/folders */}
          {contextMenu.node && contextMenu.node.path && (
            <>
              <div className="border-t border-gray-100 my-1"></div>
              <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase">
                Actions
              </div>
              
              {/* Edit Page - only for pages, not folders */}
              {!contextMenu.isFolder && (
                <button
                  onClick={handleEditFromContextMenu}
                  className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                >
                  <Edit className="h-4 w-4 text-blue-500" />
                  Edit Page
                </button>
              )}
              
              {/* Move Page - only for custom pages, not folders */}
              {!contextMenu.isFolder && (
                <button
                  onClick={handleMoveFromContextMenu}
                  className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                >
                  <MoveRight className="h-4 w-4 text-blue-500" />
                  Move to Folder
                </button>
              )}
              
              {/* Delete - for both pages and folders */}
              <button
                onClick={handleDeleteFromContextMenu}
                className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
              >
                <Trash2 className="h-4 w-4" />
                Delete {contextMenu.isFolder ? 'Folder' : 'Page'}
              </button>
            </>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deleteTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-xs w-full overflow-hidden">
            {/* Compact Header */}
            <div className="px-4 py-3 bg-gradient-to-r from-red-500 to-red-600 flex items-center gap-2">
              <Trash2 className="h-4 w-4 text-white" />
              <h3 className="text-sm font-semibold text-white">
                Delete {deleteTarget.type === 'folder' ? 'Folder' : 'Page'}
              </h3>
            </div>
            <div className="p-4">
              <p className="text-sm text-gray-600 mb-2">
                Delete <span className="font-semibold text-gray-900">"{deleteTarget.title}"</span>?
              </p>
              {deleteTarget.type === 'folder' && (
                <p className="text-xs text-amber-600 mb-2">
                  ⚠️ This will delete all contents inside.
                </p>
              )}
              <p className="text-xs text-gray-400">This cannot be undone.</p>
            </div>
            {/* Compact Footer */}
            <div className="px-4 py-3 bg-gray-50 flex gap-2">
              <button
                onClick={() => { setShowDeleteModal(false); setDeleteTarget(null); }}
                disabled={deleting}
                className="flex-1 px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deleting}
                className="flex-1 px-3 py-2 text-xs font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-1"
              >
                {deleting ? (
                  <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent"></div>
                ) : (
                  <>
                    <Trash2 className="h-3 w-3" />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Move Page Modal */}
      {showMoveModal && moveTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full overflow-hidden">
            {/* Compact Header - Website Themed */}
            <div className="px-4 py-3 bg-blue-600 flex items-center gap-2">
              <MoveRight className="h-4 w-4 text-white" />
              <h3 className="text-sm font-semibold text-white">Move Page</h3>
            </div>
            <div className="p-4">
              <p className="text-sm text-gray-600 mb-3">
                Move <span className="font-semibold text-gray-900">"{moveTarget.title}"</span> to:
              </p>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                <label className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                  selectedMoveFolder === '/' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                }`}>
                  <input
                    type="radio"
                    name="moveFolder"
                    value="/"
                    checked={selectedMoveFolder === '/'}
                    onChange={(e) => setSelectedMoveFolder(e.target.value)}
                    className="text-blue-600 focus:ring-blue-500 h-3.5 w-3.5"
                  />
                  <Folder className="h-4 w-4 text-yellow-500" />
                  <span className="text-sm text-gray-700">Root (Custom Pages)</span>
                </label>
                {folders.map(folder => (
                  <label key={folder.id} className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                    selectedMoveFolder === folder.path ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                  }`}>
                    <input
                      type="radio"
                      name="moveFolder"
                      value={folder.path}
                      checked={selectedMoveFolder === folder.path}
                      onChange={(e) => setSelectedMoveFolder(e.target.value)}
                      className="text-blue-600 focus:ring-blue-500 h-3.5 w-3.5"
                    />
                    <Folder className="h-4 w-4 text-yellow-500" />
                    <span className="text-sm text-gray-700 truncate">{folder.title}</span>
                  </label>
                ))}
                {folders.length === 0 && (
                  <p className="text-xs text-gray-500 text-center py-3">No folders available.</p>
                )}
              </div>
            </div>
            {/* Compact Footer */}
            <div className="px-4 py-3 bg-gray-50 flex gap-2">
              <button
                onClick={() => { setShowMoveModal(false); setMoveTarget(null); }}
                disabled={moving}
                className="flex-1 px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmMove}
                disabled={moving}
                className="flex-1 px-3 py-2 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-1"
              >
                {moving ? (
                  <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent"></div>
                ) : (
                  <>
                    <MoveRight className="h-3 w-3" />
                    Move
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Raw Content Generator Modal */}
      {showRawContentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden">
            {/* Sticky Header - Website Themed */}
            <div className="px-5 py-4 bg-blue-600 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <Sparkles className="h-5 w-5 text-white" />
                <h3 className="text-lg font-semibold text-white">Generate Content from Text</h3>
              </div>
              <button
                onClick={() => {
                  setShowRawContentModal(false);
                  setRawContent('');
                }}
                className="p-1.5 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {/* Scrollable Content Area */}
            <div className="flex-1 overflow-y-auto p-5">
              <p className="text-sm text-gray-600 mb-4">
                Paste raw content from any source (webpage, document, notes) and our AI will transform it into beautifully structured page content.
              </p>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Raw Content <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={rawContent}
                  onChange={(e) => setRawContent(e.target.value)}
                  placeholder="Paste your content here...

You can include:
• Text from websites
• Notes and outlines
• Product descriptions
• Service information
• Any rough content that needs formatting"
                  className="w-full h-64 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                  disabled={generatingFromRaw}
                />
                <p className="mt-2 text-xs text-gray-500">
                  {rawContent.length} characters • Minimum 50 characters recommended for best results
                </p>
              </div>
              
              {/* Web Research Option */}
              <div className="mb-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rawContentWebResearch}
                    onChange={(e) => setRawContentWebResearch(e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    disabled={generatingFromRaw}
                  />
                  <Globe className="h-4 w-4 text-gray-500" />
                  <span className="text-sm text-gray-600">Enhance content with web research</span>
                </label>
                <p className="text-xs text-gray-400 mt-1 ml-6">
                  AI will search the web for additional context to enrich your content
                </p>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-blue-900 mb-2 flex items-center gap-2">
                  <Sparkles className="h-4 w-4" />
                  How it works
                </h4>
                <ol className="text-sm text-blue-800 space-y-1.5 list-decimal list-inside">
                  <li>OpenAI analyzes and enhances your content</li>
                  {rawContentWebResearch && <li>Web research gathers additional context</li>}
                  <li>Claude structures it into organized sections</li>
                  <li>You review and customize the final result</li>
                </ol>
              </div>
            </div>
            
            {/* Sticky Footer */}
            <div className="px-5 py-4 bg-gray-50 border-t border-gray-200 flex gap-3 flex-shrink-0">
              <button
                onClick={() => {
                  setShowRawContentModal(false);
                  setRawContent('');
                  setRawContentWebResearch(false);
                }}
                disabled={generatingFromRaw}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={generateContentFromRawText}
                disabled={!rawContent.trim() || rawContent.length < 20 || generatingFromRaw}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {generatingFromRaw ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" />
                    Generate Content
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Folder Creation Modal */}
      {showFolderModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70]">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
            <div className="px-5 py-4 bg-blue-600">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <FolderPlus className="h-5 w-5" />
                Create New Folder
              </h3>
            </div>
            <div className="p-5">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Folder Name
                </label>
                <input
                  type="text"
                  value={folderName}
                  onChange={(e) => setFolderName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleFolderSubmit()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                  placeholder="e.g., Services, About Us"
                  autoFocus
                />
                <p className="mt-1.5 text-xs text-gray-500">
                  URL: {folderParentPath}{folderName ? folderName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') : 'folder-name'}
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowFolderModal(false);
                    setFolderName('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleFolderSubmit}
                  disabled={!folderName.trim()}
                  className="flex-1 px-4 py-2 bg-theme-blue text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Page Modal */}
      {showNewPageModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden">
            {/* Compact Header - Website Themed */}
            <div className="px-4 py-3 bg-blue-600 flex items-center justify-between">
              <div className="flex items-center gap-2">
                {newPageType === 'home' ? (
                  <Home className="h-4 w-4 text-white" />
                ) : (
                  <FileEdit className="h-4 w-4 text-white" />
                )}
                <h3 className="text-sm font-semibold text-white">
                  New {newPageType === 'home' ? 'Home' : 'Inner'} Page
                </h3>
              </div>
              <button
                onClick={() => setShowNewPageModal(false)}
                className="p-1 text-white/80 hover:text-white hover:bg-white/20 rounded transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Modal Body - Simple 3 Fields */}
            <div className="p-4 space-y-3">
              {/* Page Path */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Page Path <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newPageForm.path}
                  onChange={(e) => setNewPageForm({...newPageForm, path: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  placeholder="/services/my-new-page"
                  autoFocus
                />
                <p className="text-xs text-gray-400 mt-1">URL path for this page</p>
              </div>

              {/* Page Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Page Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newPageForm.title}
                  onChange={(e) => setNewPageForm({...newPageForm, title: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  placeholder="My New Page"
                />
              </div>

              {/* Layout */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Layout</label>
                <select
                  value={newPageForm.layout}
                  onChange={(e) => setNewPageForm({...newPageForm, layout: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
                >
                  {newPageType === 'home' ? (
                    <option value="home">Home Page Layout</option>
                  ) : (
                    <>
                      <option value="default">Default (Centered Hero)</option>
                      <option value="image_right">Image Right</option>
                      <option value="full_width">Full Width</option>
                    </>
                  )}
                </select>
              </div>
            </div>

            {/* Compact Footer */}
            <div className="px-4 py-3 bg-gray-50 flex gap-2">
              <button
                onClick={() => setShowNewPageModal(false)}
                className="flex-1 px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleNewPageSubmit}
                disabled={saving || !newPageForm.path || !newPageForm.title}
                className="flex-1 px-3 py-2 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-1"
              >
                {saving ? (
                  <div className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full" />
                ) : (
                  <>
                    <Plus className="h-3 w-3" />
                    Create
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Toast */}
      {notification.show && (
        <div className={`fixed bottom-4 right-4 px-4 py-3 rounded-lg shadow-lg ${
          notification.type === 'success' ? 'bg-green-500' : 'bg-red-500'
        } text-white z-[80]`}>
          {notification.message}
        </div>
      )}
    </div>
  );
};

export default CMSEditor;
